/**
 * Data model + pure helpers for the 2D SVG Diagram tool.
 *
 * Unlike the 3D SVG Diagram tool, node positions live directly in the
 * exported SVG's coordinate space (the viewBox) — there is no camera or
 * projection step, so the editing canvas renders the same coordinates that
 * end up in the exported SVG.
 */

export interface Vec2 {
    x: number;
    y: number;
}

export type DiagramTool =
    | "select"
    | "place-node"
    | "node-brush"
    | "multi-node-brush"
    | "mega-node-brush"
    | "interact"
    | "connect"
    | "connect-selection"
    | "scale"
    | "rotate"
    | "ellipse"
    | "line"
    | "arc"
    | "rect"
    | "polygon"
    | "heat-zone"
    | "group";

/**
 * How the nodes a single "node brush" stroke drops are wired together:
 * - `sequence`: a chain A→B→C→…→A (the last links back to the first).
 * - `complete`: every node connected to every other node.
 * (See {@link NodeBrushOptions}.) The brush is the first "composite subtool" —
 * one canvas action that emits several primitive elements (nodes, connections,
 * and optionally a group) in a single undo step.
 */
export type NodeBrushConnectMode = "sequence" | "complete";

/**
 * How a node-brush stroke arranges its `count` nodes within `radius` of the
 * press point:
 * - `sunflower`: even phyllotaxis scatter filling the whole disk.
 * - `random`: uniform random scatter within the disk (seeded per stroke).
 * - `ring`: a single evenly-spaced ring on the radius.
 * - `grid`: a square grid clipped to the disk.
 * - `spiral`: an Archimedean spiral winding out to the radius.
 * (See {@link NodeBrushOptions}.)
 */
export type NodeBrushLayout =
    | "sunflower"
    | "random"
    | "ring"
    | "grid"
    | "spiral";

/**
 * Parameters for the node-brush composite subtool. A single click scatters
 * `count` nodes within `radius` of the press point (arranged per `layout`),
 * optionally connects them (per `connectMode`) and/or groups them.
 * `connect`/`group` gate whether the respective step runs at all.
 *
 * Future composite subtools (a brush that uses a node brush; a brush aware of
 * the previous stroke's nodes for cross-stroke connections) build on the same
 * single-action-per-stroke shape.
 */
export interface NodeBrushOptions {
    /** Spread radius (canvas units) the scattered nodes fall within. */
    radius: number;
    /** How many nodes a single stroke drops (>= 1). */
    count: number;
    /** How the dropped nodes are positioned within the radius. */
    layout: NodeBrushLayout;
    /** Whether to add connections among the dropped nodes. */
    connect: boolean;
    /** Wiring pattern used when `connect` is on. */
    connectMode: NodeBrushConnectMode;
    /** Whether to wrap the dropped nodes in a new group. */
    group: boolean;
}

/**
 * Parameters for the multi-node-brush composite subtool — a "brush of brushes".
 * A single click stamps `count` node-brush strokes whose centers are scattered
 * within `spreadRadius` of the press point (arranged per `layout`, reusing the
 * same layout machinery a single brush uses for its nodes). Each stroke is a
 * full node brush, driven by the shared {@link NodeBrushOptions}, so the same
 * per-cluster size/connect/group settings apply to every stamped cluster.
 *
 * This is the "a brush that uses a node brush" composite subtool foreshadowed
 * by {@link NodeBrushOptions}: it emits several brush strokes (each itself
 * several nodes/connections/groups) in a single undo step.
 */
export interface MultiNodeBrushOptions {
    /** Spread radius (canvas units) the stamped stroke centers fall within. */
    spreadRadius: number;
    /** How many node-brush strokes a single click stamps (>= 1). */
    count: number;
    /** How the stamped stroke centers are positioned within `spreadRadius`. */
    layout: NodeBrushLayout;
    /** Whether to wrap every node from all stamped strokes in one new group. */
    groupAll: boolean;
}

/**
 * How a mega-node-brush distributes its `count` multi-node-brush stamps:
 * - `scatter`: scatter the stamp centers within `spreadRadius` of the press
 *   point (arranged per `layout`) — every stamp uses the same spread radius.
 * - `concentric`: stack every stamp on the press point itself, growing each
 *   successive stamp's multi-node spread radius by `radiusStep` (and its stroke
 *   count by `countStep`) so the stamps nest as concentric rings around one
 *   center, with outer rings holding more clusters.
 * (See {@link MegaNodeBrushOptions}.)
 */
export type MegaNodeBrushMode = "scatter" | "concentric";

/**
 * Parameters for the mega-node-brush composite subtool — one level up again, a
 * "brush of multi-node brushes". A single click stamps `count` multi-node-brush
 * stamps (distributed per `mode`), and each stamp is itself a full multi-node
 * brush (driven by the shared {@link MultiNodeBrushOptions}, which in turn
 * drives a node brush per stroke via {@link NodeBrushOptions}).
 *
 * Same single-action-per-click shape as the brushes below it: it emits many
 * multi-brush stamps — each itself several node-brush strokes — in one undo
 * step. The recursion stops here; deeper nesting would reuse this same pattern.
 */
export interface MegaNodeBrushOptions {
    /** How the stamps are distributed (scattered vs. concentric). */
    mode: MegaNodeBrushMode;
    /** How many multi-node-brush stamps a single click stamps (>= 1). */
    count: number;
    /** Whether to wrap every node from all stamps in one new group. */
    groupAll: boolean;
    /** `scatter` mode: radius (canvas units) the stamp centers fall within. */
    spreadRadius: number;
    /** `scatter` mode: how the stamp centers are positioned within `spreadRadius`. */
    layout: NodeBrushLayout;
    /**
     * `concentric` mode: how much each successive stamp's multi-node spread
     * radius grows (canvas units), starting from the multi-node brush's own
     * spread radius for the innermost stamp.
     */
    radiusStep: number;
    /**
     * `concentric` mode: how many extra node-brush strokes each successive ring
     * adds over the previous one, starting from the multi-node brush's own
     * stroke count for the innermost ring. 0 keeps every ring the same count;
     * positive values make outer rings hold gradually more clusters so density
     * stays even as the radius grows.
     */
    countStep: number;
}

/**
 * How the Connect-selection subtool wires the currently selected nodes:
 * - `complete`: every selected node to every other (the original "press C"
 *   behavior). Combined with a max-distance threshold this becomes a proximity
 *   graph — only near-enough pairs link.
 * - `sequence`: a chain following selection order (1→2→3→…), skipping any link
 *   whose endpoints fall outside the distance thresholds.
 * - `nearest`: each selected node to its single closest eligible neighbor, so a
 *   scatter wires into a sparse nearest-neighbor mesh.
 * (See {@link ConnectSelectionOptions}.)
 */
export type ConnectSelectionMode = "complete" | "sequence" | "nearest";

/**
 * Parameters for the Connect-selection subtool. The subtool connects the
 * currently selected nodes per `mode`, but only links a pair whose center
 * distance passes the (optional) proximity thresholds — `minDistance` rejects
 * pairs that are too close, `maxDistance` rejects pairs that are too far apart.
 * Each threshold is gated by its own `*Enabled` flag, so either, both, or
 * neither can constrain the wiring.
 */
export interface ConnectSelectionOptions {
    /** Wiring pattern across the selected nodes. */
    mode: ConnectSelectionMode;
    /** When set, skip pairs closer than `minDistance` (canvas units). */
    minDistanceEnabled: boolean;
    /** Lower distance bound (canvas units), applied when `minDistanceEnabled`. */
    minDistance: number;
    /** When set, skip pairs farther than `maxDistance` (canvas units). */
    maxDistanceEnabled: boolean;
    /** Upper distance bound (canvas units), applied when `maxDistanceEnabled`. */
    maxDistance: number;
}

/**
 * What the node-interact subtool does to the nodes in range of the one it's
 * dispatched on:
 * - `attract`: pull each nearby node toward the dispatched-on node.
 * - `repel`: push each nearby node away from it (toward the influence edge).
 * - `connect`: add connections from the dispatched-on node out across its
 *   neighbors within range (no movement); see {@link NodeInteractConnectMode}.
 * (See {@link NodeInteractOptions}.)
 */
export type NodeInteractAction = "attract" | "repel" | "connect";

/**
 * How a `connect` node-interact pulse wires the dispatched-on node to its
 * in-range neighbors:
 * - `direct`: a hub — link the dispatched node straight to every node in range.
 * - `bridge`: grow a tree out from the dispatched node (a minimum spanning tree
 *   over it and its in-range neighbors), so each node joins via its nearest
 *   already-connected node. Far nodes are reached through the nodes between them
 *   and the dispatched one rather than by a single long direct edge.
 * (See {@link NodeInteractOptions} / `nodeConnectPulsePairs`.)
 */
export type NodeInteractConnectMode = "direct" | "bridge";

export const NODE_INTERACT_CONNECT_MODES: NodeInteractConnectMode[] = [
    "direct",
    "bridge",
];

export const NODE_INTERACT_CONNECT_MODE_LABELS: Record<
    NodeInteractConnectMode,
    string
> = {
    direct: "Direct (hub to each)",
    bridge: "Bridge (through nearest)",
};

/**
 * How the node-interact subtool's influence decays with distance from the
 * dispatched-on node — full at its center (distance 0), zero at the influence
 * `radius` edge. The named curve maps normalized distance `t` (0..1) to a 0..1
 * weight that scales `strength` per node:
 * - `constant`: full influence everywhere inside the radius (hard cutoff).
 * - `linear`: ramps straight down, 1 → 0.
 * - `smooth`: smoothstep ease — gentle near the center and the edge.
 * - `ease-in`: concentrated near the center, falling off fast (`(1-t)²`).
 * - `ease-out`: a broad plateau near the center, dropping near the edge (`1-t²`).
 * (See {@link NodeInteractOptions} / `interactFalloffWeight`.)
 */
export type InteractFalloff =
    | "constant"
    | "linear"
    | "smooth"
    | "ease-in"
    | "ease-out";

export const INTERACT_FALLOFFS: InteractFalloff[] = [
    "constant",
    "linear",
    "smooth",
    "ease-in",
    "ease-out",
];

export const INTERACT_FALLOFF_LABELS: Record<InteractFalloff, string> = {
    constant: "Constant (hard edge)",
    linear: "Linear",
    smooth: "Smooth (ease)",
    "ease-in": "Concentrated (near center)",
    "ease-out": "Broad (plateau)",
};

/**
 * Parameters for the node-interact subtool. Unlike the brushes (which act on a
 * canvas point), this subtool is *dispatched on a node*: clicking a node applies
 * one pulse that, for `attract`/`repel`, moves every other node within `radius`
 * toward it or away from it — each by a fraction of the way given by `strength`
 * scaled by the `falloff` weight for its distance, so a click nudges nodes
 * part-way and repeated clicks accumulate. For `connect` the pulse instead wires
 * the dispatched node to its in-range neighbors per `connectMode` (no movement;
 * `strength`/`falloff` don't apply). One click is one undo step.
 */
export interface NodeInteractOptions {
    /** Whether nearby nodes are pulled in, pushed out, or connected. */
    action: NodeInteractAction;
    /** Influence radius (canvas units); nodes beyond it are untouched. */
    radius: number;
    /**
     * Fraction of the move applied at full influence per dispatch, 0..1. At 1 an
     * attracted node at the center of influence snaps onto the dispatched node;
     * smaller values nudge it part-way (repeat clicks accumulate). Used by
     * `attract`/`repel` only.
     */
    strength: number;
    /**
     * How the influence decays from the center (full) to the radius edge (0).
     * Used by `attract`/`repel` only.
     */
    falloff: InteractFalloff;
    /** How a `connect` pulse wires the dispatched node to its in-range neighbors. */
    connectMode: NodeInteractConnectMode;
    /**
     * Position tolerance (canvas units) for matching the dispatched node's
     * symmetric counterpart, when symmetry is active. The pulse re-fires from
     * the counterpart node found within this distance of the host's mirrored
     * position, so a layout that's only approximately symmetric (hand-placed /
     * nudged off the exact canvas-center reflection) still mirrors the action.
     * Defaults to the tight exact-match tolerance ({@link SYMMETRY_MATCH_TOLERANCE});
     * raise it to bridge a known drift.
     */
    symmetryTolerance: number;
}

/**
 * Which group operation the Group subtool performs on the nodes a marquee
 * grabs, set by the modifier held while the rectangle is drawn:
 * - `create`: (no modifier) make a fresh group from the selected ungrouped
 *   nodes (nodes already in a group are ignored).
 * - `add`: (Shift) when the marquee grabs exactly one grouped node and one
 *   ungrouped node, add the ungrouped one to that node's group.
 * - `remove`: (Alt) drop each selected node from the last group it was added
 *   to (the last group whose `nodeIds` contains it).
 */
export type GroupToolMode = "create" | "add" | "remove";

// --- Fills ----------------------------------------------------------------
// Shape/group interiors can be painted with either a flat color or a tiled
// object SVG (a "pattern fill"). `Fill` is the discriminated union stored on
// every fillable shape; `null` means no fill (stroke-only). Pattern fills point
// at a document-level `DiagramPattern` by id so the same pattern is shared and
// edited in one place (mirrors connection presets / effects).

export type Fill =
    | { type: "solid"; color: string }
    | { type: "pattern"; patternId: string };

/**
 * One object SVG that can populate a pattern's tiles. `svg` is a complete
 * `<svg>…</svg>` document (a built-in preset or an uploaded file), parsed the
 * same way connection objects are. Shares the shape of `ConnectionObject`.
 */
export interface PatternObject {
    id: string;
    name: string;
    /** Complete `<svg>...</svg>` string for this object's artwork. */
    svg: string;
}

/** How a pattern's tiles cycle through its objects across the super-tile grid. */
export type PatternOrder = "sequence" | "random";

export const PATTERN_ORDERS: PatternOrder[] = ["sequence", "random"];

export const PATTERN_ORDER_LABELS: Record<PatternOrder, string> = {
    sequence: "In order",
    random: "Random",
};

/**
 * A document-level tiled-SVG pattern, referenced by `{type:"pattern"}` fills.
 *
 * A pattern carries one or more `objects`. With a single object the tile is just
 * that object. With several, the tiles alternate between them: the objects are
 * laid into a small NxM "super-tile" block (one object per cell) that repeats —
 * `sequence` fills the block in order, `random` shuffles the assignment (stable
 * across re-renders). SVG `<pattern>` repeats a single tile, so the alternation
 * lives inside that one repeating block rather than an infinite per-cell grid.
 * The tiling fields lay the cells out on a grid in SVG user units.
 */
export interface DiagramPattern {
    id: string;
    name: string;
    /** Object SVGs cycled across the tiles (alternate when more than one). */
    objects: PatternObject[];
    /** How tiles pick objects across the super-tile: in order or shuffled. */
    order: PatternOrder;
    /** Size (SVG user units) each object's larger dimension is scaled to per cell. */
    tileSize: number;
    /** Horizontal gap added between adjacent cells, in SVG user units (>= 0). */
    spacingX: number;
    /** Vertical gap added between adjacent cells, in SVG user units (>= 0). */
    spacingY: number;
    /** Rotation of the whole tiled grid, in degrees. */
    rotationDeg: number;
    /** Optional solid color painted behind the tiles (null = transparent tiles). */
    backgroundColor: string | null;
}

/**
 * Coerce a persisted shape `strokeStyleId` into a valid value: kept only when
 * it still names a known stroke-style id, so deleting a style can't leave a
 * shape pointing at a missing one (it falls back to the shape's own plain
 * stroke). Anything else becomes `null`. Shared by every standalone shape's
 * coercion (and the effect/outline coercion).
 */
export function coerceStrokeStyleId(
    raw: unknown,
    knownStyleIds: ReadonlySet<string>,
): string | null {
    return typeof raw === "string" && knownStyleIds.has(raw) ? raw : null;
}

export const DEFAULT_FILL_COLOR = "#a0c4ff";

/** A flat-color fill from a color string. */
export function solidFill(color: string): Fill {
    return { type: "solid", color };
}

/**
 * Coerce a persisted/legacy fill value into a `Fill | null`. Legacy fills were
 * plain color strings (or null); those become solid fills. Pattern fills are
 * kept only when they still name a known pattern id (so deleting a pattern
 * can't leave a dangling reference — those fall back to no fill).
 */
export function coerceFill(
    raw: unknown,
    knownPatternIds?: ReadonlySet<string>,
): Fill | null {
    if (raw == null) return null;
    if (typeof raw === "string") {
        return raw && raw !== "transparent" ? solidFill(raw) : null;
    }
    if (typeof raw === "object") {
        const f = raw as Partial<Fill>;
        if (f.type === "solid" && typeof f.color === "string") {
            return f.color && f.color !== "transparent"
                ? solidFill(f.color)
                : null;
        }
        if (f.type === "pattern" && typeof f.patternId === "string") {
            if (knownPatternIds && !knownPatternIds.has(f.patternId)) {
                return null;
            }
            return { type: "pattern", patternId: f.patternId };
        }
    }
    return null;
}

// --- Effects (reusable node decorations) ---------------------------------
// Effects are defined once at the document level and referenced by id from
// any number of nodes (like connection presets). Each effect kind renders
// something around the node it's applied to. New kinds (concentric circles,
// SVG stamps, …) slot in by extending `Effect` and `renderNodeEffect`.

export type EffectKind = "radial-emitter" | "concentric-rings";

export const EFFECT_KINDS: EffectKind[] = [
    "radial-emitter",
    "concentric-rings",
];

export const EFFECT_LABELS: Record<EffectKind, string> = {
    "radial-emitter": "Radial emitter",
    "concentric-rings": "Concentric rings",
};

interface BaseEffect {
    id: string;
    name: string;
    kind: EffectKind;
}

/** Draws rays from an inner ring to an outer ring around the node. */
export interface RadialEmitterEffect extends BaseEffect {
    kind: "radial-emitter";
    rayCount: number;
    innerRadius: number;
    outerRadius: number;
    /** Stroke style applied to the rays (null = default). */
    strokeStyleId: string | null;
}

/** Draws `ringCount` evenly spaced circles around the node. */
export interface ConcentricRingsEffect extends BaseEffect {
    kind: "concentric-rings";
    ringCount: number;
    /** Radius of the innermost ring, in SVG user units. */
    innerRadius: number;
    /** Center-to-center spacing between adjacent rings, in SVG user units. */
    spacing: number;
    /** Stroke style applied to the rings (null = default). */
    strokeStyleId: string | null;
}

export type Effect = RadialEmitterEffect | ConcentricRingsEffect;

// --- Group properties ----------------------------------------------------

export type GroupPropertyKind = "outline";

/** How softened corners are drawn: a circular arc, or a straight chamfer. */
export type CornerStyle = "round" | "bevel";

export const CORNER_STYLES: CornerStyle[] = ["round", "bevel"];

/**
 * Shape of a group outline:
 * - `hull`: convex hull tightly wrapping the member nodes (any angle).
 * - `angular`: a convex enclosure whose edges may only point in one of
 *   `bendCount` evenly spaced directions. bendCount 2 = bounding box,
 *   4 = octagon, higher = a rounder enclosure that hugs the nodes tighter.
 * - `ortho`: tight hull with every edge locked to 90-degree (H/V) directions.
 * - `diag`: tight hull with every edge locked to 45-degree / 90-degree
 *   directions.
 */
export type OutlineShape = "hull" | "angular" | "ortho" | "diag";

export const OUTLINE_SHAPES: OutlineShape[] = [
    "hull",
    "angular",
    "ortho",
    "diag",
];

/**
 * The stylable fields of a group outline, independent of which group it wraps.
 * Shared by `OutlineProperty` (a group's own outline) and `GroupOutlinePreset`
 * (a reusable, named bundle of these fields referenced by many groups at once).
 */
export interface OutlineStyleFields {
    padding: number;
    /** Stroke style applied to the outline (null = default). */
    strokeStyleId: string | null;
    /** Closed encircling hull vs. open polyline. */
    closed: boolean;
    /** Geometry used to build the surrounding shape. */
    shape: OutlineShape;
    /**
     * For `angular`: number of allowed edge directions (>= 2). 2 = box,
     * 4 = octagon, higher = rounder. Ignored for `hull`.
     */
    bendCount: number;
    /** Corner fillet radius for the shape, in SVG units (0 = sharp). */
    cornerRadius: number;
    /** Whether softened corners are rounded (arc) or beveled (chamfer). */
    cornerStyle: CornerStyle;
    /** Interior fill (solid color or tiled pattern); null = no fill. */
    fill: Fill | null;
}

/**
 * A reusable, named group-outline style. Groups link to one by id via
 * `OutlineProperty.presetId`; while linked, the preset's fields drive the
 * outline's look, so editing the preset updates every linked group at once.
 */
export interface GroupOutlinePreset extends OutlineStyleFields {
    id: string;
    name: string;
}

/** Encircles the group's member nodes with a (padded) outline. */
export interface OutlineProperty extends OutlineStyleFields {
    kind: "outline";
    /**
     * When set to a known `GroupOutlinePreset` id, that preset's fields drive
     * this outline's look (its own fields below are ignored until unlinked).
     * null/undefined = standalone outline using its own fields.
     */
    presetId?: string | null;
}

export type GroupProperty = OutlineProperty;

/**
 * Resolve a group outline's effective style: when it links a known preset, the
 * preset's fields win; otherwise the outline's own fields are used. Render, the
 * live viewer, and the inspector all read through this so a linked group always
 * reflects its preset. The returned object keeps the outline's `kind`/`presetId`
 * so it can still flow through `groupOutlinePath` and rendering unchanged.
 */
export function resolveGroupOutline(
    outline: OutlineProperty,
    presets: GroupOutlinePreset[],
): OutlineProperty {
    if (!outline.presetId) return outline;
    const preset = presets.find((p) => p.id === outline.presetId);
    if (!preset) return outline;
    const { id: _id, name: _name, ...fields } = preset;
    return { ...outline, ...fields };
}

export type NodeShape = "circle" | "square" | "diamond" | "triangle";

export const NODE_SHAPES: NodeShape[] = [
    "circle",
    "square",
    "diamond",
    "triangle",
];

export interface DiagramNode {
    id: string;
    label: string;
    /** Layer this node lives on (see `DiagramLayer`). */
    layerId: string;
    position: Vec2;
    /** Marker half-extent (radius), in SVG user units. */
    radius: number;
    color: string;
    shape: NodeShape;
    /** When false the node (and anything attached to it) is omitted on export. */
    visible: boolean;
    /** Ids of document-level effects applied to this node. */
    effectIds: string[];
    /**
     * When true, this node's fill color shifts toward the nearest heat zone's
     * color by proximity (see `DiagramHeatZone` / `applyHeatColor`). Off by
     * default: the node keeps its plain `color`.
     */
    heatReactive?: boolean;
    /** Pattern that surrounds this node with clones of a template node. */
    spawn?: SpawnPattern | null;
    /**
     * Set when this node is a clone produced by another node's `spawn`
     * pattern. Clones are followers: their position is derived from the
     * host's pattern, and their appearance mirrors the template node.
     */
    spawnedFrom?: { hostId: string; templateId: string } | null;
}

// --- Spawn patterns (clone a template node around a host node) -----------
// A "host" node (A) can surround itself with clones of a separate "template"
// node (B): the clones copy B's color/size/shape/effects and are positioned
// by the pattern, but are otherwise plain nodes (no recursive spawning).
// Clones are regenerated from scratch whenever the host moves or the pattern/
// template changes, so they never drift out of sync — the same "derived
// follower" approach the mirror system already uses for symmetry.

export type SpawnPatternKind = "square" | "circle";

export const SPAWN_PATTERN_KINDS: SpawnPatternKind[] = ["square", "circle"];

export const SPAWN_PATTERN_LABELS: Record<SpawnPatternKind, string> = {
    square: "Square ring",
    circle: "Circle ring",
};

interface BaseSpawnPattern {
    kind: SpawnPatternKind;
    /** Id of the node whose appearance is cloned. */
    templateId: string;
}

/**
 * A square ring of clones around the host: `countPerSide` clones run along
 * each side of a square centered on the host, `gap` apart (edge-to-edge,
 * accounting for the template's radius). Any slot that would land on the
 * host's own position is skipped.
 */
export interface SquareSpawnPattern extends BaseSpawnPattern {
    kind: "square";
    countPerSide: number;
    gap: number;
}

/**
 * A circular ring of `count` clones evenly spaced around the host at
 * `radius` units from its center.
 */
export interface CircleSpawnPattern extends BaseSpawnPattern {
    kind: "circle";
    count: number;
    radius: number;
}

export type SpawnPattern = SquareSpawnPattern | CircleSpawnPattern;

/**
 * How a connection is routed between its two node centers.
 * - `straight`: a single direct line.
 * - `orthogonal`: a staircase of axis-aligned (90-degree) segments.
 * - `diagonal`: a staircase alternating axis-aligned and 45-degree segments.
 * - `arc`: a semicircular arc bulging to one side of the straight chord.
 * - `sine`: a sine wave undulating to both sides of the straight chord,
 *   sampled into a dense polyline so it composes with offsetting/objects.
 */
export type ConnectionRouting =
    | "straight"
    | "orthogonal"
    | "diagonal"
    | "arc"
    | "sine";

export const CONNECTION_ROUTINGS: ConnectionRouting[] = [
    "straight",
    "orthogonal",
    "diagonal",
    "arc",
    "sine",
];

/**
 * What a connection draws along its routed path:
 * - `stroke`: the line itself (the only mode before objects were added).
 * - `objects`: clones of SVG object(s) populated along the path, but no line.
 * - `both`: the stroked line AND objects on top of it.
 */
export type ConnectionRenderKind = "stroke" | "objects" | "both";

export const CONNECTION_RENDER_KINDS: ConnectionRenderKind[] = [
    "stroke",
    "objects",
    "both",
];

export const CONNECTION_RENDER_KIND_LABELS: Record<
    ConnectionRenderKind,
    string
> = {
    stroke: "Stroke (line)",
    objects: "Objects along path",
    both: "Stroke + objects",
};

/** How a populated object is turned relative to the path. */
export type ConnectionObjectRotation = "align" | "fixed";

export const CONNECTION_OBJECT_ROTATIONS: ConnectionObjectRotation[] = [
    "align",
    "fixed",
];

export const CONNECTION_OBJECT_ROTATION_LABELS: Record<
    ConnectionObjectRotation,
    string
> = {
    align: "Align to path",
    fixed: "Fixed angle",
};

/**
 * Order objects are picked in as they're populated along a connection path.
 * `sequence` cycles `objectSvgs` in order (the default); `random` picks a
 * deterministic but shuffled object per instance (seeded so the viewer and the
 * string exporter agree, and so it stays stable across re-renders).
 */
export type ConnectionObjectOrder = "sequence" | "random";

export const CONNECTION_OBJECT_ORDERS: ConnectionObjectOrder[] = [
    "sequence",
    "random",
];

export const CONNECTION_OBJECT_ORDER_LABELS: Record<
    ConnectionObjectOrder,
    string
> = {
    sequence: "In order",
    random: "Random",
};

/**
 * One SVG used to populate an object-bearing connection. `svg` is a complete
 * `<svg>…</svg>` document (a built-in preset or an uploaded file). When a preset
 * carries several, instances alternate between them along the path.
 */
export interface ConnectionObject {
    id: string;
    name: string;
    /** Complete `<svg>...</svg>` string. */
    svg: string;
}

/**
 * Reusable *stroke* style: the appearance of a stroked path, independent of how
 * that path is routed. Referenced directly by standalone shapes, node effects,
 * and group outlines (which style their own geometry), and indirectly by
 * connections via `ConnectionPreset.strokeStyleId` (a connection pairs a stroke
 * style with a routing). Carries color/width/dash, parallel-line bundling, and
 * the objects-along-path settings — everything that decorates a path without
 * dictating its shape.
 */
export interface StrokeStyle {
    id: string;
    name: string;
    color: string;
    strokeWidth: number;
    /** SVG stroke-dasharray; "" = solid. */
    dash: string;
    /**
     * Number of parallel lines drawn along the path (>= 1). For a shape outline
     * the parallel lines ride the shape's own outline; for a connection they
     * ride the routed path.
     */
    lineCount: number;
    /** Perpendicular spacing between adjacent parallel lines, in SVG units. */
    lineSpacing: number;
    /**
     * What is drawn along the path: just the stroked line (`stroke`, the
     * default), SVG objects populated along the path (`objects`), or both.
     */
    renderKind: ConnectionRenderKind;
    /**
     * SVGs cloned along the path for `objects`/`both` render kinds. Empty falls
     * back to a stroke. When several are present, instances alternate between
     * them in order along the path.
     */
    objectSvgs: ConnectionObject[];
    /**
     * Optional fill color applied to every object populated along the path. When
     * set, it overrides the `fill` of the object's own shapes (the same recolor
     * Object-along-Path does). Empty/undefined leaves each object's own colors.
     */
    objectColor?: string;
    /** Arc-length spacing between adjacent object instances, in SVG units (> 0). */
    objectSpacing: number;
    /**
     * Target on-canvas size (SVG units) of each object's larger dimension; the
     * object's own viewBox is scaled to match. Mirrors Object-along-Path's size.
     */
    objectScale: number;
    /**
     * Order objects are picked in along the path: cycling `objectSvgs` in order
     * (`sequence`, the default) or a deterministic shuffle (`random`). Only
     * matters when more than one object is present.
     */
    objectOrder: ConnectionObjectOrder;
    /** How each object is turned: aligned to the path tangent or a fixed angle. */
    objectRotation: ConnectionObjectRotation;
    /**
     * Rotation in degrees. Added on top of the tangent angle for `align`, or used
     * outright for `fixed`.
     */
    objectAngleDeg: number;
    /**
     * When true, each object populated along the path shifts its color toward the
     * nearest heat zone's color by proximity to that zone (see `DiagramHeatZone`
     * / `applyHeatColor`), using `objectColor` (or the object's own color) as the
     * base. Off by default: every object uses the flat `objectColor`.
     */
    heatReactive?: boolean;
}

/**
 * Reusable connection *routing* preset: how a connection's path is shaped
 * between its two node centers, plus a reference to the `StrokeStyle` that
 * paints it. Only connections use this; standalone shapes/effects/outlines
 * reference a `StrokeStyle` directly (they have their own geometry, so routing
 * is meaningless to them).
 */
export interface ConnectionPreset {
    id: string;
    name: string;
    /** Stroke style that paints this connection's routed path. */
    strokeStyleId: string;
    /** Routing shape of the connection line. */
    routing: ConnectionRouting;
    /**
     * Number of corners in `orthogonal`/`diagonal` routing (>= 1). Ignored for
     * `straight`.
     */
    bendCount: number;
    /** Corner fillet radius for bent routings, in SVG units (0 = sharp). */
    cornerRadius: number;
    /** Whether softened corners are rounded (arc) or beveled (chamfer). */
    cornerStyle: CornerStyle;
    /**
     * For `arc` routing: which side of the chord the arc bulges toward.
     * Flipping it mirrors the arc to the opposite side.
     */
    arcFlip: boolean;
    /**
     * For `arc` routing with multiple parallel lines: when true the arcs keep a
     * constant `lineSpacing` apart all the way to the endpoints (offset start/
     * end points); when false they're concentric and converge on the same two
     * endpoints, fanning out only in the middle.
     */
    arcSpacedEnds: boolean;
    /**
     * For `arc` routing with `arcSpacedEnds`: when true the spaced endpoints are
     * pushed *along* the chord (in opposite directions on the two nodes) so the
     * arcs nest concentrically and their endpoints stack along the connection
     * line — for a horizontal pair they spread horizontally instead of stacking
     * vertically. Ignored when `arcSpacedEnds` is false.
     */
    arcEndsAlongChord: boolean;
    /**
     * For `arc` routing: portion of a full circle the arc subtends between its
     * (pinned) endpoints, 0..1. 0.5 is a semicircle; below 0.5 flattens toward
     * a straight line; above 0.5 bulges past a half-circle into a deeper loop.
     */
    arcSweep: number;
    /**
     * For `sine` routing: peak perpendicular displacement from the chord, in SVG
     * units. The wave swings `sineAmplitude` to each side of the straight line.
     */
    sineAmplitude: number;
    /**
     * For `sine` routing: length of one full wave cycle along the chord, in SVG
     * units. Smaller values pack more oscillations between the two nodes. Clamped
     * to a small minimum so it never divides by zero.
     */
    sineWavelength: number;
    /**
     * When true, connections using this preset are joined into smooth chains:
     * where two such connections meet at a shared node and have the same routing
     * and parallel-line count (via their stroke styles), the line arriving at
     * that node flows continuously into the one leaving it (a single bend
     * through the node) instead of two segments dead-ending at its center. See
     * `bridgedConnectionChains`. Default off.
     */
    bridge: boolean;
}

/**
 * A `ConnectionPreset` (routing) merged with its referenced `StrokeStyle`
 * (stroke fields) into one flat object — the shape every connection geometry/
 * render helper (`connectionPaths`, `connectionLinePolylines`,
 * `bridgedConnectionChains`, the string exporter) consumes. Built by
 * `resolveConnectionStyle` so the viewer and the exporter agree on the merge.
 */
export type ResolvedConnectionStyle = Omit<ConnectionPreset, "strokeStyleId"> &
    Omit<StrokeStyle, "id" | "name">;


export interface Connection {
    id: string;
    /**
     * Layer this connection lives on (see `DiagramLayer`). A connection always
     * sits on the shared layer of its two endpoint nodes — connections can't
     * span layers.
     */
    layerId: string;
    sourceId: string;
    targetId: string;
    presetId: string;
    /**
     * Per-connection override for `arc` routing's bulge side. When undefined the
     * connection follows the preset's `arcFlip`; `true`/`false` forces the arc
     * to one side regardless of the preset default. Ignored for non-arc routing.
     */
    arcFlip?: boolean;
    /**
     * Per-connection override for `arc` routing's sweep depth (0..1). When
     * undefined the connection follows the preset's `arcSweep`; a number forces
     * that bulge depth regardless of the preset default. Ignored for non-arc
     * routing. See `ConnectionPreset.arcSweep` for how the value maps to depth.
     */
    arcSweep?: number;
}

// --- Ellipses ------------------------------------------------------------
// A standalone vector shape (unlike nodes, which are point markers). An
// ellipse is defined by its center and two radii. `startAngle`/`sweep` carve a
// partial arc out of the outline: by default a full ellipse, but reducing the
// sweep draws only part of the ring (an open arc stroke), optionally rotated by
// the start angle. Ellipses are not part of the node graph — they don't
// connect, spawn, group, or mirror — they're decorative geometry.

export interface DiagramEllipse {
    id: string;
    label: string;
    /** Layer this shape lives on (see `DiagramLayer`). */
    layerId: string;
    /** Center in SVG user units (same coordinate space as node positions). */
    center: Vec2;
    /** Horizontal radius (half-width), in SVG user units. */
    rx: number;
    /** Vertical radius (half-height), in SVG user units. */
    ry: number;
    /** Outline (stroke) color. */
    color: string;
    strokeWidth: number;
    /** SVG stroke-dasharray; "" = solid. */
    dash: string;
    /**
     * Optional stroke style applied to this outline. When set, the outline is
     * drawn with that style's color/width/dash, parallel lines, and
     * objects-along-path (riding the shape's own outline), overriding
     * `color`/`strokeWidth`/`dash`. null/undefined = plain stroke. Shared
     * convention with rect/polygon/line/arc.
     */
    strokeStyleId?: string | null;
    /** Interior fill (solid color or tiled pattern); null = no fill. */
    fill: Fill | null;
    /** When false the ellipse is omitted on export (faint in the editor). */
    visible: boolean;
    /** Arc start angle in degrees, measured clockwise from the +X axis. */
    startAngle: number;
    /** Fraction of the full ring drawn, 0..1 (1 = a closed ellipse). */
    sweep: number;
    /**
     * Rotation of the whole shape about its center, in degrees clockwise. The
     * geometry (rx/ry, the partial-arc carve) is built axis-aligned and spun by
     * this angle at render time via an SVG `rotate(θ cx cy)` transform, so the
     * stored center / radii stay axis-aligned for editing and snapping.
     */
    rotation: number;
}

// --- Lines & arcs --------------------------------------------------------
// Two-point standalone vector shapes, drawn by dragging a start→end point
// (like ellipses, these are decorative geometry — not part of the node graph).
// A line is the straight segment between its endpoints. An arc is a circular
// arc that shares those endpoints but bulges to one side; its bulge side and
// depth are tunable the same way connection `arc` routing is (`flip`/`sweep`).

interface BaseTwoPointShape {
    id: string;
    label: string;
    /** Layer this shape lives on (see `DiagramLayer`). */
    layerId: string;
    /** Start endpoint in SVG user units (same space as node positions). */
    start: Vec2;
    /** End endpoint in SVG user units. */
    end: Vec2;
    /** Outline (stroke) color. */
    color: string;
    strokeWidth: number;
    /** SVG stroke-dasharray; "" = solid. */
    dash: string;
    /**
     * Optional stroke style applied to this shape's outline, overriding
     * `color`/`strokeWidth`/`dash` (see `DiagramEllipse.strokeStyleId`). For the
     * open shapes (line/arc) the style's stroke/parallel-lines/objects ride the
     * shape's own segment or arc; for the closed rect the outline ring is used.
     */
    strokeStyleId?: string | null;
    /** When false the shape is omitted on export (faint in the editor). */
    visible: boolean;
}

/** A straight line segment between two points (no geometry beyond endpoints). */
export type DiagramLine = BaseTwoPointShape;

/**
 * An axis-aligned rectangle defined by two opposite corners (`start`/`end`,
 * the drag's press and release points). Like the ellipse it can be filled and
 * its corners softened — rounded (arc) or beveled (chamfer) per `cornerStyle`,
 * by `cornerRadius` SVG units.
 */
export interface DiagramRect extends BaseTwoPointShape {
    /** Interior fill (solid color or tiled pattern); null = no fill. */
    fill: Fill | null;
    /** Corner fillet radius, in SVG units (0 = sharp). */
    cornerRadius: number;
    /** Whether softened corners are rounded (arc) or beveled (chamfer). */
    cornerStyle: CornerStyle;
    /**
     * Rotation of the box about its center, in degrees clockwise. The box stays
     * axis-aligned in `start`/`end` (so corner handles, fills, and corner radii
     * keep working) and is spun by this angle at render time via an SVG
     * `rotate(θ cx cy)` transform.
     */
    rotation: number;
}

/** A circular arc bulging to one side of the chord between its two endpoints. */
export interface DiagramArc extends BaseTwoPointShape {
    /** Which side of the chord the arc bulges toward (flip = the other side). */
    flip: boolean;
    /**
     * Portion of a full circle the arc subtends between its (pinned) endpoints,
     * 0..1. 0.5 is a semicircle; below flattens toward a straight line; above
     * bulges past a half-circle into a deeper loop. Matches `ConnectionPreset.
     * arcSweep`.
     */
    sweep: number;
}

// --- Polygons ------------------------------------------------------------
// A standalone closed shape defined by an ordered list of vertices the user
// places one at a time (unlike the two-point shapes, which are dragged out).
// Like the rect it can be filled and its corners softened — rounded (arc) or
// beveled (chamfer) per `cornerStyle`, by `cornerRadius` SVG units. Polygons
// are not part of the node graph — they're decorative geometry.

export interface DiagramPolygon {
    id: string;
    label: string;
    /** Layer this shape lives on (see `DiagramLayer`). */
    layerId: string;
    /** Ordered vertices in SVG user units (same space as node positions). */
    points: Vec2[];
    /** Outline (stroke) color. */
    color: string;
    strokeWidth: number;
    /** SVG stroke-dasharray; "" = solid. */
    dash: string;
    /**
     * Optional stroke style applied to this outline, overriding
     * `color`/`strokeWidth`/`dash` (see `DiagramEllipse.strokeStyleId`). The
     * style's parallel lines / objects ride the closed polygon ring.
     */
    strokeStyleId?: string | null;
    /** Interior fill (solid color or tiled pattern); null = no fill. */
    fill: Fill | null;
    /** When false the polygon is omitted on export (faint in the editor). */
    visible: boolean;
    /** Corner fillet radius, in SVG units (0 = sharp). */
    cornerRadius: number;
    /** Whether softened corners are rounded (arc) or beveled (chamfer). */
    cornerStyle: CornerStyle;
}

// --- Heat zones ----------------------------------------------------------
// An editor-only, rectangular region of "heat". Heat zones draw nothing into
// the exported SVG themselves; instead, elements that opt in (a heat-reactive
// stroke style's objects-along-path, or a heat-reactive node) shift their color
// toward the nearest influencing zone's color by proximity. A zone's influence
// is full inside its rectangle and falls off linearly to nothing `radius` units
// outside the edge. Like the standalone rect, a zone is defined by two opposite
// corners (`start`/`end`, the drag's press and release points).

export interface DiagramHeatZone {
    id: string;
    label: string;
    /** Layer this zone lives on (see `DiagramLayer`); governs editor visibility. */
    layerId: string;
    /** One corner of the zone rectangle, in SVG user units. */
    start: Vec2;
    /** The opposite corner, in SVG user units. */
    end: Vec2;
    /** The "hot" color reactive elements blend toward inside this zone. */
    color: string;
    /**
     * Distance (SVG units) the influence extends beyond the rectangle edge.
     * Influence is 1 inside the rectangle and ramps linearly to 0 at `radius`.
     */
    radius: number;
    /** When false the zone is hidden in the editor (it never exports regardless). */
    visible: boolean;
}

export interface NodeGroup {
    id: string;
    name: string;
    /**
     * Layer this group lives on (see `DiagramLayer`). All of a group's member
     * nodes share this layer — groups can't span layers.
     */
    layerId: string;
    nodeIds: string[];
    properties: GroupProperty[];
}

// --- Layers --------------------------------------------------------------
// Every element (node, connection, standalone shape) and group belongs to
// exactly one layer. Layers provide draw-order stacking (the order in
// `DiagramDocument.layers` is the z-order: the first layer is drawn at the
// back, the last at the front), a per-layer visibility toggle (hidden layers
// are omitted from the editor and export), a lock toggle (locked layers'
// elements can't be selected/edited on the canvas), and one "active" layer
// that newly drawn elements are placed on. Groups and connections may only be
// made between elements that share a layer.

export interface DiagramLayer {
    id: string;
    name: string;
    /** When false, this layer's elements are hidden in the editor and on export. */
    visible: boolean;
    /** When true, this layer's elements can't be selected or edited on the canvas. */
    locked: boolean;
}


export type SymmetryAxis = "x" | "y";

export interface SymmetryState {
    x: boolean;
    y: boolean;
    /** Editor-only: show a vertical line guide down the canvas's horizontal center. */
    guideX: boolean;
    /** Editor-only: show a horizontal line guide across the canvas's vertical center. */
    guideY: boolean;
}

export interface DiagramDocument {
    /**
     * Layers in draw order (first = back, last = front). Every element and
     * group references one of these by `layerId`. Always non-empty.
     */
    layers: DiagramLayer[];
    nodes: DiagramNode[];
    connections: Connection[];
    /** Reusable stroke styles, referenced by shapes/effects/outlines and presets. */
    strokeStyles: StrokeStyle[];
    presets: ConnectionPreset[];
    /** Reusable node effects, referenced by `DiagramNode.effectIds`. */
    effects: Effect[];
    /** Reusable tiled-SVG fill patterns, referenced by `{type:"pattern"}` fills. */
    patterns: DiagramPattern[];
    groups: NodeGroup[];
    /** Reusable group-outline styles, linked by `OutlineProperty.presetId`. */
    groupOutlinePresets: GroupOutlinePreset[];
    /** Standalone ellipse shapes (not part of the node graph). */
    ellipses: DiagramEllipse[];
    /** Standalone straight-line shapes (not part of the node graph). */
    lines: DiagramLine[];
    /** Standalone arc shapes (not part of the node graph). */
    arcs: DiagramArc[];
    /** Standalone rectangle shapes (not part of the node graph). */
    rects: DiagramRect[];
    /** Standalone polygon shapes (not part of the node graph). */
    polygons: DiagramPolygon[];
    /**
     * Editor-only heat zones. They emit no geometry on export; they only recolor
     * heat-reactive elements (stroke-style objects, nodes) by proximity.
     */
    heatZones: DiagramHeatZone[];
    symmetry: SymmetryState;
}

// --- ids ------------------------------------------------------------------

let idCounter = 0;
export function newId(prefix: string): string {
    const uuid =
        typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : `${Date.now()}-${(idCounter += 1)}`;
    return `${prefix}-${uuid}`;
}
